import {
  LanguageModelV3,
  LanguageModelV3CallOptions,
  SharedV3Warning,
  LanguageModelV3Content,
  LanguageModelV3FinishReason,
  LanguageModelV3ProviderTool,
  LanguageModelV3StreamPart,
  LoadSettingError,
  LanguageModelV3GenerateResult,
  LanguageModelV3StreamResult,
} from "@ai-sdk/provider";
import {
  AutoTokenizer,
  AutoModelForCausalLM,
  AutoProcessor,
  AutoModelForImageTextToText,
  StoppingCriteria,
  env,
  type PretrainedModelOptions,
  type ProgressInfo,
} from "@huggingface/transformers";
import { convertToTransformersMessages } from "../utils/convert-to-transformers-message";
import type { TransformersMessage } from "../utils/convert-to-transformers-message";
import type {
  ModelInstance,
  GenerationOptions,
} from "./transformers-js-worker-types";
import {
  parseJsonFunctionCalls,
  createUnsupportedSettingWarning,
  createUnsupportedToolWarning,
  isFunctionTool,
  processToolCallStream,
  type ParsedToolCall,
  type ToolDefinition,
  type DownloadProgressCallback,
} from "@browser-ai/shared";
import {
  createMainThreadGenerationStream,
  createWorkerGenerationStream,
} from "../utils/generation-stream";

declare global {
  interface Navigator {
    gpu?: unknown;
  }
}

export type TransformersJSModelId = string;

export interface TransformersJSModelSettings extends Pick<
  PretrainedModelOptions,
  "device" | "dtype"
> {
  /**
   * Optional ONNX external data format toggle.
   * Keep undefined to use Transformers.js defaults.
   */
  use_external_data_format?: boolean;
  /**
   * Progress callback for model initialization
   */
  initProgressCallback?: DownloadProgressCallback;
  /**
   * Raw progress callback from Transformers.js
   */
  rawInitProgressCallback?: (progress: ProgressInfo) => void;
  /**
   * Whether this is a vision model
   * @default false
   */
  isVisionModel?: boolean;
  /**
   * Optional Web Worker to run the model off the main thread
   */
  worker?: Worker;
  /**
   * Optional path to load local models from.
   * Overrides the transformers.js env.localModelPath setting.
   * @see https://huggingface.co/docs/transformers.js/api/env
   * @example '~/models' or '/path/to/models'
   */
  localModelPath?: string;
  /**
   * Optional directory to use for caching files.
   * Overrides the transformers.js env.cacheDir setting.
   * @see https://huggingface.co/docs/transformers.js/api/env
   * @example '~/model-cache' or '/path/to/cache'
   */
  cacheDir?: string;
}

/**
 * Check if we're running in a browser environment
 */
export function isBrowserEnvironment(): boolean {
  return typeof window !== "undefined";
}

/**
 * Check if we're running in a server environment (Node.js)
 */
export function isServerEnvironment(): boolean {
  return typeof window === "undefined" && typeof process !== "undefined";
}

/**
 * Check if the browser supports TransformersJS with optimal performance
 * Returns true if the browser has WebGPU or WebAssembly support
 * @returns true if the browser supports TransformersJS, false otherwise
 */
export function doesBrowserSupportTransformersJS(): boolean {
  if (!isBrowserEnvironment()) {
    return false;
  }

  // Check for WebGPU support for better performance
  if (typeof navigator !== "undefined" && navigator.gpu) {
    return true;
  }

  // Check for WebAssembly support as fallback
  if (typeof WebAssembly !== "undefined") {
    return true;
  }

  return false;
}

// Simplified config - just extend the settings with modelId
interface ModelConfig extends TransformersJSModelSettings {
  modelId: TransformersJSModelId;
}

class InterruptableStoppingCriteria extends StoppingCriteria {
  interrupted = false;

  interrupt() {
    this.interrupted = true;
  }

  reset() {
    this.interrupted = false;
  }

  _call(input_ids: number[][], scores: number[][]): boolean[] {
    return new Array(input_ids.length).fill(this.interrupted);
  }
}

export class TransformersJSLanguageModel implements LanguageModelV3 {
  readonly specificationVersion = "v3";
  readonly modelId: TransformersJSModelId;
  readonly provider = "transformers-js";

  private readonly config: ModelConfig;
  private modelInstance?: ModelInstance;
  private isInitialized = false;
  private initializationPromise?: Promise<void>;
  private stoppingCriteria = new InterruptableStoppingCriteria();
  private workerReady = false;

  constructor(
    modelId: TransformersJSModelId,
    options: TransformersJSModelSettings = {},
  ) {
    this.modelId = modelId;
    this.config = {
      modelId,
      device: "auto",
      dtype: "auto",
      isVisionModel: false,
      ...options,
    };
  }

  readonly supportedUrls: Record<string, RegExp[]> = {
    // TransformersJS doesn't support URLs natively
  };

  private async getSession(
    onInitProgress?: DownloadProgressCallback,
  ): Promise<ModelInstance> {
    if (this.modelInstance && this.isInitialized) {
      return this.modelInstance;
    }

    if (this.initializationPromise) {
      await this.initializationPromise;
      if (this.modelInstance) {
        return this.modelInstance;
      }
    }

    this.initializationPromise = this._initializeModel(onInitProgress);
    await this.initializationPromise;

    if (!this.modelInstance) {
      throw new LoadSettingError({
        message: "Model initialization failed",
      });
    }

    return this.modelInstance;
  }

  private async _initializeModel(
    onInitProgress?: DownloadProgressCallback,
  ): Promise<void> {
    try {
      const {
        isVisionModel,
        device,
        dtype,
        localModelPath,
        cacheDir,
        use_external_data_format,
      } = this.config;

      // Configure transformers.js environment settings
      if (localModelPath) {
        env.localModelPath = localModelPath;
      }
      if (cacheDir) {
        env.cacheDir = cacheDir;
      }

      const progress_callback = this.createProgressTracker(onInitProgress);

      // Set device based on environment
      const resolvedDevice = this.resolveDevice(
        device as string,
      ) as PretrainedModelOptions["device"];
      const resolvedDtype = this.resolveDtype(
        dtype as string,
      ) as PretrainedModelOptions["dtype"];

      // Create model instance based on type
      if (isVisionModel) {
        const [processor, model] = await Promise.all([
          AutoProcessor.from_pretrained(this.modelId, { progress_callback }),
          AutoModelForImageTextToText.from_pretrained(this.modelId, {
            dtype: resolvedDtype,
            device: resolvedDevice,
            ...(use_external_data_format !== undefined
              ? { use_external_data_format }
              : {}),
            progress_callback,
          }),
        ]);
        this.modelInstance = [processor, model];
      } else {
        const [tokenizer, model] = await Promise.all([
          AutoTokenizer.from_pretrained(this.modelId, {
            progress_callback,
          }),
          AutoModelForCausalLM.from_pretrained(this.modelId, {
            dtype: resolvedDtype,
            device: resolvedDevice,
            ...(use_external_data_format !== undefined
              ? { use_external_data_format }
              : {}),
            progress_callback,
          }),
        ]);
        this.modelInstance = [tokenizer, model];

        // Warm up text models (skip in server environment to reduce initialization time)
        if (isBrowserEnvironment()) {
          const dummyInputs = tokenizer("Hello");
          await model.generate({ ...dummyInputs, max_new_tokens: 1 });
        }
      }

      onInitProgress?.(1.0);
      this.isInitialized = true;
    } catch (error) {
      this.modelInstance = undefined;
      this.isInitialized = false;
      this.initializationPromise = undefined;

      throw new LoadSettingError({
        message: `Failed to initialize TransformersJS model: ${error instanceof Error ? error.message : "Unknown error"}`,
      });
    }
  }

  private resolveDevice(device?: string): string {
    if (device && device !== "auto") {
      return device;
    }

    if (isServerEnvironment()) {
      // In server environment, prefer CPU unless explicitly set
      return "cpu";
    }

    // In browser environment, auto-detect WebGPU support
    if (
      isBrowserEnvironment() &&
      typeof navigator !== "undefined" &&
      navigator.gpu
    ) {
      return "webgpu";
    }

    return "cpu";
  }

  private resolveDtype(dtype?: string): string {
    if (dtype && dtype !== "auto") {
      return dtype;
    }

    return "auto";
  }

  private createProgressTracker(onInitProgress?: DownloadProgressCallback) {
    const fileProgress = new Map<string, { loaded: number; total: number }>();

    return (p: ProgressInfo) => {
      // Pass through raw progress
      this.config.rawInitProgressCallback?.(p);

      if (!onInitProgress) return;

      // Type guard to check if p has file property
      const progressWithFile = p as ProgressInfo & {
        file?: string;
        loaded?: number;
        total?: number;
      };
      const file = progressWithFile.file;

      if (!file) return;

      if (p.status === "progress" && file) {
        fileProgress.set(file, {
          loaded: progressWithFile.loaded || 0,
          total: progressWithFile.total || 0,
        });
      } else if (p.status === "done" && file) {
        const prev = fileProgress.get(file);
        if (prev?.total) {
          fileProgress.set(file, { loaded: prev.total, total: prev.total });
        }
      }

      // Calculate overall progress
      let totalLoaded = 0;
      let totalBytes = 0;
      for (const { loaded, total } of fileProgress.values()) {
        if (total > 0) {
          totalLoaded += loaded;
          totalBytes += total;
        }
      }

      if (totalBytes > 0) {
        onInitProgress(Math.min(1, totalLoaded / totalBytes));
      }
    };
  }

  private getArgs({
    prompt,
    maxOutputTokens,
    temperature,
    topP,
    topK,
    frequencyPenalty,
    presencePenalty,
    stopSequences,
    responseFormat,
    seed,
    tools,
    toolChoice,
    providerOptions,
  }: Parameters<LanguageModelV3["doGenerate"]>[0]): {
    messages: TransformersMessage[];
    warnings: SharedV3Warning[];
    generationOptions: GenerationOptions;
    functionTools: ToolDefinition[];
    enableThinking: boolean;
  } {
    const warnings: SharedV3Warning[] = [];
    // Filter and warn about unsupported tools
    const functionTools: ToolDefinition[] = (tools ?? [])
      .filter(isFunctionTool)
      .map((tool) => ({
        name: tool.name,
        description: tool.description,
        parameters: tool.inputSchema,
      }));

    const unsupportedTools = (tools ?? []).filter(
      (tool): tool is LanguageModelV3ProviderTool => !isFunctionTool(tool),
    );

    for (const tool of unsupportedTools) {
      warnings.push(
        createUnsupportedToolWarning(
          tool,
          "Only function tools are supported by TransformersJS",
        ),
      );
    }

    // Add warnings for unsupported settings
    if (frequencyPenalty != null) {
      warnings.push(
        createUnsupportedSettingWarning(
          "frequencyPenalty",
          "Frequency penalty is not supported by TransformersJS",
        ),
      );
    }

    if (presencePenalty != null) {
      warnings.push(
        createUnsupportedSettingWarning(
          "presencePenalty",
          "Presence penalty is not supported by TransformersJS",
        ),
      );
    }

    if (stopSequences != null) {
      warnings.push(
        createUnsupportedSettingWarning(
          "stopSequences",
          "Stop sequences are not supported by TransformersJS",
        ),
      );
    }

    if (responseFormat?.type === "json") {
      warnings.push(
        createUnsupportedSettingWarning(
          "responseFormat",
          "JSON response format is not supported by TransformersJS",
        ),
      );
    }

    if (seed != null) {
      warnings.push(
        createUnsupportedSettingWarning(
          "seed",
          "Seed is not supported by TransformersJS",
        ),
      );
    }

    if (toolChoice != null) {
      warnings.push(
        createUnsupportedSettingWarning(
          "toolChoice",
          "toolChoice is not supported by TransformersJS",
        ),
      );
    }

    // Convert messages to TransformersJS format
    const messages = convertToTransformersMessages(
      prompt,
      this.config.isVisionModel,
    );

    const transformersJsOptions = providerOptions?.["transformers-js"] as
      | Record<string, unknown>
      | undefined;
    const enableThinking =
      (transformersJsOptions?.enableThinking as boolean) ?? false;

    const generationOptions: GenerationOptions = {
      max_new_tokens: maxOutputTokens || (enableThinking ? 8192 : 4096),
      temperature: temperature ?? 0.7,
      top_p: topP,
      top_k: topK,
      do_sample: temperature === undefined || temperature > 0,
    };

    return {
      messages,
      warnings,
      generationOptions,
      functionTools,
      enableThinking,
    };
  }

  /**
   * Check the availability of the TransformersJS model
   */
  public async availability(): Promise<
    "unavailable" | "downloadable" | "available"
  > {
    // If using a worker (browser only), reflect worker readiness instead of main-thread state
    if (this.config.worker && isBrowserEnvironment()) {
      return this.workerReady ? "available" : "downloadable";
    }

    // In server environment, workers are not used
    if (isServerEnvironment() && this.config.worker) {
      // Ignore worker config on server and use main thread
    }

    if (this.isInitialized) {
      return "available";
    }

    return "downloadable";
  }

  /**
   * Creates a session with download progress monitoring
   */
  public async createSessionWithProgress(
    onDownloadProgress?: DownloadProgressCallback,
  ): Promise<TransformersJSLanguageModel> {
    // If a worker is provided and we're in browser environment, initialize the worker
    // (and forward progress) instead of initializing the model on the main thread
    // to avoid double-initialization/downloads.
    if (this.config.worker && isBrowserEnvironment()) {
      await this.initializeWorker(onDownloadProgress);
      return this;
    }

    // In server environment or when no worker is provided, use main thread
    await this._initializeModel(onDownloadProgress);
    return this;
  }

  /**
   * Generates a complete text response using TransformersJS
   */
  public async doGenerate(
    options: LanguageModelV3CallOptions,
  ): Promise<LanguageModelV3GenerateResult> {
    const {
      messages,
      warnings,
      generationOptions,
      functionTools,
      enableThinking,
    } = this.getArgs(options);

    const useWorker = this.config.worker && isBrowserEnvironment();

    // Initialize worker if needed
    if (useWorker) {
      await this.initializeWorker();
    }

    try {
      // Create the appropriate generation stream
      const generationStream = useWorker
        ? createWorkerGenerationStream({
            worker: this.config.worker!,
            messages,
            generationOptions,
            tools: functionTools,
            enableThinking,
            abortSignal: options.abortSignal,
          })
        : createMainThreadGenerationStream({
            modelInstance: await this.getSession(
              this.config.initProgressCallback,
            ),
            messages,
            generationOptions,
            tools: functionTools,
            isVisionModel: this.config.isVisionModel,
            enableThinking,
            stoppingCriteria: this.stoppingCriteria,
            abortSignal: options.abortSignal,
          });

      // Collect all generated text
      let generatedText = "";
      let lastUsage: { inputTokens?: number; outputTokens?: number } = {};
      let workerToolCalls: ParsedToolCall[] = [];

      for await (const event of generationStream) {
        if (event.type === "delta") {
          generatedText += event.delta;
        } else if (event.type === "complete") {
          lastUsage = event.usage || {};
          if (event.toolCalls) {
            workerToolCalls = event.toolCalls;
          }
        }
      }

      // Parse for tool calls - prefer worker-parsed ones if available
      const { toolCalls: parsedToolCalls, textContent } =
        parseJsonFunctionCalls(generatedText);
      const toolCalls =
        workerToolCalls.length > 0 ? workerToolCalls : parsedToolCalls;

      if (toolCalls.length > 0) {
        const toolCallsToEmit = toolCalls.slice(0, 1);
        const parts: LanguageModelV3Content[] = [];

        if (textContent) {
          parts.push({
            type: "text",
            text: textContent,
          });
        }

        for (const call of toolCallsToEmit) {
          parts.push({
            type: "tool-call",
            toolCallId: call.toolCallId,
            toolName: call.toolName,
            input: JSON.stringify(call.args ?? {}),
          });
        }

        return {
          content: parts,
          finishReason: { unified: "tool-calls", raw: "tool-calls" },
          usage: {
            inputTokens: {
              total: lastUsage.inputTokens,
              noCache: undefined,
              cacheRead: undefined,
              cacheWrite: undefined,
            },
            outputTokens: {
              total: lastUsage.outputTokens,
              text: undefined,
              reasoning: undefined,
            },
          },
          request: { body: { messages, ...generationOptions } },
          warnings,
        };
      }

      const content: LanguageModelV3Content[] = [
        {
          type: "text",
          text: textContent || generatedText,
        },
      ];

      return {
        content,
        finishReason: { unified: "stop", raw: "stop" },
        usage: {
          inputTokens: {
            total: lastUsage.inputTokens,
            noCache: undefined,
            cacheRead: undefined,
            cacheWrite: undefined,
          },
          outputTokens: {
            total: lastUsage.outputTokens,
            text: undefined,
            reasoning: undefined,
          },
        },
        request: { body: { messages, ...generationOptions } },
        warnings,
      };
    } catch (error) {
      throw new Error(
        `TransformersJS generation failed: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      );
    }
  }

  private async initializeWorker(
    onInitProgress?: DownloadProgressCallback,
  ): Promise<void> {
    if (!this.config.worker) return;

    // If already ready, optionally emit completion progress
    if (this.workerReady) {
      if (onInitProgress) onInitProgress(1);
      return;
    }

    const worker = this.config.worker;

    await new Promise<void>((resolve, reject) => {
      const trackProgress = this.createProgressTracker(onInitProgress);

      const onMessage = (e: MessageEvent) => {
        const msg = e.data;
        if (!msg) return;

        // Forward raw download progress events coming from @huggingface/transformers running in the worker
        if (msg && typeof msg === "object" && "status" in msg) {
          if (msg.status === "ready") {
            worker.removeEventListener("message", onMessage);
            this.workerReady = true;
            if (onInitProgress) onInitProgress(1);
            resolve();
            return;
          }
          if (msg.status === "error") {
            worker.removeEventListener("message", onMessage);
            reject(
              new Error(String(msg.data || "Worker initialization failed")),
            );
            return;
          }

          // Only track file-related messages (raw ProgressInfo events)
          const msgWithFile = msg as ProgressInfo & { file?: string };
          if (msgWithFile.file) trackProgress(msg as ProgressInfo);
        }
      };

      worker.addEventListener("message", onMessage);
      worker.postMessage({
        type: "load",
        data: {
          modelId: this.modelId,
          dtype: this.config.dtype,
          device: this.config.device,
          use_external_data_format: this.config.use_external_data_format,
          isVisionModel: this.config.isVisionModel,
        },
      });
    });
  }

  /**
   * Generates a streaming text response using TransformersJS
   */
  public async doStream(
    options: LanguageModelV3CallOptions,
  ): Promise<LanguageModelV3StreamResult> {
    const {
      messages,
      warnings,
      generationOptions,
      functionTools,
      enableThinking,
    } = this.getArgs(options);

    const useWorker = this.config.worker && isBrowserEnvironment();

    // Initialize worker if needed
    if (useWorker) {
      await this.initializeWorker();
    }

    const self = this;
    const textId = "text-0";

    const stream = new ReadableStream<LanguageModelV3StreamPart>({
      async start(controller) {
        controller.enqueue({ type: "stream-start", warnings });

        let textStarted = false;

        const emitTextDelta = (delta: string) => {
          if (!delta) return;
          if (!textStarted) {
            controller.enqueue({ type: "text-start", id: textId });
            textStarted = true;
          }
          controller.enqueue({ type: "text-delta", id: textId, delta });
        };

        try {
          // Create the appropriate generation stream
          const generationStream = useWorker
            ? createWorkerGenerationStream({
                worker: self.config.worker!,
                messages,
                generationOptions,
                tools: functionTools,
                enableThinking,
                abortSignal: options.abortSignal,
              })
            : createMainThreadGenerationStream({
                modelInstance: await self.getSession(
                  self.config.initProgressCallback,
                ),
                messages,
                generationOptions,
                tools: functionTools,
                isVisionModel: self.config.isVisionModel,
                enableThinking,
                stoppingCriteria: self.stoppingCriteria,
                abortSignal: options.abortSignal,
              });

          let lastUsage: { inputTokens?: number; outputTokens?: number } = {};

          const chunks = (async function* () {
            for await (const event of generationStream) {
              if (event.type === "delta") yield event.delta;
              else if (event.type === "complete") lastUsage = event.usage || {};
            }
          })();

          const result = await processToolCallStream(
            chunks,
            emitTextDelta,
            controller,
          );

          if (result.trailingText) {
            emitTextDelta(result.trailingText);
          }

          if (textStarted) {
            controller.enqueue({ type: "text-end", id: textId });
          }

          const finishReason: LanguageModelV3FinishReason =
            result.toolCallDetected
              ? { unified: "tool-calls", raw: "tool-calls" }
              : { unified: "stop", raw: "stop" };

          controller.enqueue({
            type: "finish",
            finishReason,
            usage: {
              inputTokens: {
                total: lastUsage.inputTokens,
                noCache: undefined,
                cacheRead: undefined,
                cacheWrite: undefined,
              },
              outputTokens: {
                total: lastUsage.outputTokens,
                text: undefined,
                reasoning: undefined,
              },
            },
          });
          controller.close();
        } catch (error) {
          controller.enqueue({ type: "error", error });
          controller.close();
        }
      },
    });

    return {
      stream,
      request: { body: { messages, ...generationOptions } },
    };
  }
}
